import {
  FlowStep,
  FundingOption,
  FundingOptionSectionItem,
  FundingOriginInput,
  UpsertFlowStepInput,
} from '@varo/graphql-schema-types';
import { Stack, useRouter } from 'expo-router';
import { navigateToDirectDepositSetup } from 'features/directDeposit/navigation/navigateToDirectDepositSetup';
import DirectDepositLoader from 'features/directDeposit/screens/DirectDepositLoader';
import useFundingNavigation from 'features/onboarding/hooks/useFundingNavigation';
import useGetFlowStepId from 'features/onboarding/hooks/useGetFlowStepId';
import useGetFundingOptionsSectioned from 'features/onboarding/hooks/useGetFundingOptionsSectioned';
import useUpsertFlowStep from 'features/onboarding/hooks/useUpsertFlowStep';
import { redirectUserToTargetRoute } from 'features/onboarding/utils/redirectUserToTargetRoute';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { parseGQLAnalyticsEvent } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import Button from 'shared/components/vxl/Button/Button';
import SectionDivider from 'shared/components/vxl/divider/SectionDivider/SectionDivider';
import { IconKeyType } from 'shared/components/vxl/icon/Icon/Icon';
import ListItemGroup, {
  ListItemGroupItem,
} from 'shared/components/vxl/ListItemGroup/ListItemGroup';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import TitleXLText from 'shared/components/vxl/typography/primary/title/TitleXLText';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useDirectDepositGateway } from 'shared/hooks/pinwheel/useDirectDepositGateway/useDirectDepositGateway';
import { AbortError } from 'shared/hooks/useCancellableMutation';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

const ITEM_ICONS: Record<string, IconKeyType> = {
  APPLE_PAY: 'APPLE_PAY',
  CASH_DEPOSIT: 'DEPOSIT_CASH',
  DIRECT_DEPOSIT: 'EARLY_PAYDAY',
  DCF: 'CARD',
  CASH_APP_PAYPAL_VENMO: 'MOBILE_DEPOSIT',
  BANK_TRANSFER: 'BANK',
  GOOGLE_PAY: 'GOOGLE_PAY',
};

const FundingScreenV2 = () => {
  const router = useRouter();
  const { fundingOptionsData, error, fetching } = useGetFundingOptionsSectioned(
    {
      origin: FundingOriginInput.Onboarding,
    },
  );
  const { defaultMessage, graphqlErrorsTitle } = useDefaultErrorStrings();
  const sectionsData = fundingOptionsData?.sections;
  const skipData = fundingOptionsData?.skipButton;
  const headLineData = fundingOptionsData?.title;
  const { segmentClient } = useAnalyticsConfiguration();

  const { t: translatedText } = useTranslation('onboarding');
  const { directDepositGatewayFlag } = useDirectDepositGateway();
  const { navigateFundingAction, pinWheelLoading, directDepositIntroProps } =
    useFundingNavigation();
  const [showComponent, setShowComponent] = useState(true);

  const flowStepId = useGetFlowStepId();

  const { isUpsertFlowStepLoading, submitUpsertFlowStep } = useUpsertFlowStep();

  useViewDidFocusScreenAnalyticsEvent({
    key: '/onboarding/funding/screenViewed',
    customAttributes: {
      screen_name: 'Funding Screen V2',
      screen_url: '/onboarding/funding-v2',
    },
  });

  const transformFundingOptionsToListItems = (
    options: FundingOptionSectionItem[],
  ): ListItemGroupItem[] =>
    options.map((option) => {
      const iconKey = ITEM_ICONS[option.id] ?? null;
      return {
        keyId: option.id,
        primaryCopy: option.title,
        secondaryCopy: option.description,
        leadingAccessory: iconKey
          ? {
              id: 'icon',
              name: VXLComponentId.ICON_AVATAR,
              props: { iconKey },
            }
          : undefined,
        onPress: () => {
          handleOptionPress(option);
        },
        showsChevron: true,
      };
    });

  const submitFlowStep = async (didSkip: boolean) => {
    const input: UpsertFlowStepInput = {
      id: flowStepId,
      intentBasedOnboarding: {
        selection: undefined,
        didSkip,
      },
    };
    try {
      const result = await submitUpsertFlowStep({ input });
      if (!result.error) {
        const flowStep = result.data?.upsertFlowStep as FlowStep;
        redirectUserToTargetRoute(flowStep);
      }
      if (result.error) {
        logErrorWithContext('Funding options v2 submitUpsertFlowStep error:', {
          featureArea: FeatureArea.ONBOARDING,
          responsibleTeam: ResponsibleTeam.CUSTOMER_ACQUISITION,
          severity: ErrorSeverity.LOW,
          additionalContext: {
            error:
              result.error instanceof Error
                ? result.error.message
                : String(result.error),
          },
        });
        return;
      }
    } catch (error) {
      if (error instanceof AbortError) {
        return;
      }
      throw error;
    }
  };
  const handleOptionPress = (option: FundingOptionSectionItem) => {
    const { name, properties } = parseGQLAnalyticsEvent(option.clickAnalytics);
    void segmentClient.logEvent(name, properties);
    if (option.type === FundingOption.DirectDeposit) {
      setShowComponent(true);
    }
    navigateFundingAction(option.type, (outcome) => {
      if (outcome?.success) {
        void submitFlowStep(false);
      }
    });
  };

  useEffect(() => {
    if (error) {
      logErrorWithContext('Funding options v2 error from GQL:', {
        featureArea: FeatureArea.ONBOARDING,
        responsibleTeam: ResponsibleTeam.CUSTOMER_ACQUISITION,
        severity: ErrorSeverity.LOW,
        additionalContext: {
          error,
        },
      });
    }
  }, [error]);

  useEffect(() => {
    const shouldShowIntroScreen =
      directDepositGatewayFlag && !!directDepositIntroProps && showComponent;

    if (!shouldShowIntroScreen || !directDepositIntroProps) {
      return;
    }

    setShowComponent(false);
    navigateToDirectDepositSetup({
      entry: 'onboarding-funding-v2',
      flowId: flowStepId,
      sourceName: directDepositIntroProps.sourceName ?? 'onboarding_funding',
      abCohort: directDepositIntroProps.abCohort ?? '',
      isCoinLoader: false,
    });
  }, [
    directDepositGatewayFlag,
    directDepositIntroProps,
    showComponent,
    flowStepId,
  ]);

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      {sectionsData ? (
        <View style={styles.container}>
          {skipData?.text ? (
            <NavigationBar
              title=""
              trailingAccessories={
                <Button
                  label={skipData.text}
                  size="SMALL"
                  type="TEXT"
                  width="HUG"
                  onPress={() => {
                    const { name, properties } = parseGQLAnalyticsEvent(
                      skipData.clickAnalytics,
                    );
                    void segmentClient.logEvent(name, properties);
                    void submitFlowStep(true);
                  }}
                />
              }
            />
          ) : null}
          <ScrollView style={styles.scrollContainer}>
            {headLineData ? (
              <TitleXLText style={styles.headlineText}>
                {headLineData}
              </TitleXLText>
            ) : null}
            <View>
              {sectionsData.map((section) => {
                const listItems = transformFundingOptionsToListItems(
                  section.options,
                );
                return (
                  <View key={section.key}>
                    {section.title ? (
                      <SectionDivider
                        textSize="large"
                        labelText={section.title}
                      />
                    ) : null}
                    <ListItemGroup items={listItems} />
                  </View>
                );
              })}
            </View>
          </ScrollView>
        </View>
      ) : null}
      <ScreenTransitionOverlay
        isLoading={fetching || isUpsertFlowStepLoading || pinWheelLoading}
        LoadingComponent={
          <DirectDepositLoader
            isLoadingConditions={fetching || isUpsertFlowStepLoading}
            isPinwheelLoading={pinWheelLoading}
            defaultLoadingComponent={
              <FullScreenLoading
                isCoinLoader
                titleText={translatedText('loading.titleText')}
              />
            }
          />
        }
        ErrorComponent={
          error ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={graphqlErrorsTitle}
              messageBody={defaultMessage}
              primaryButton={{
                title: translatedText('errors.goToHomePageButtonLabel'),
                onPress: () => {
                  router.push('/');
                },
              }}
            />
          ) : null
        }
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    zIndex: 1,
  },
  headlineText: {
    padding: theme.spacing.m,
    textAlign: 'left',
    marginBottom: theme.spacing.s,
  },
  scrollContainer: {
    flex: 1,
    paddingTop: 0,
  },
});
export default FundingScreenV2;
